import React, { useEffect } from "react";
import Navigation from "./Navigation";
import ProfessionalTimeline from "./ProfessionalTimeline";

const AboutPage = () => {
  useEffect(() => {
    document.title = "About - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  const skills = {
    "AI & Strategy": ["Generative AI", "AI Ethics", "Web Strategy", "Product Strategy"],
    "UX / Product": [
      "User Research",
      "Wireframing",
      "Prototyping",
      "Usability Testing",
      "Information Architecture",
      "Interaction Design",
      "Visual Design",
      "Design Systems",
    ],
    Delivery: ["Project Management", "Stakeholder Facilitation", "Cross-functional Collaboration"],
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-20 pb-10 bg-gradient-to-r from-blue-50 to-indigo-50 section-padding">
        <div className="container-narrow text-center space-y-4">
          <h1 className="heading-hero text-gray-900 animate-fade-in">About Me</h1>
          <div className="w-20 h-1 bg-blue-600 mx-auto" />
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed animate-slide-up">
            Lead Web Strategist focused on AI-powered UX and product experiences that turn 0-to-1 ideas
            into scalable, conversion-driven web platforms.
          </p>
        </div>
      </section>

      {/* Summary + Education + Certifications */}
      <section className="section-padding bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 space-y-16">
          {/* Summary */}
          <div className="space-y-6">
            <h2 className="heading-section">Who I Am</h2>
            <div className="space-y-4 text-body">
              <p>
                I blend UX, web strategy, and AI to help teams ship experiences that are both intuitive and
                tied directly to business outcomes. My background in business marketing gives me a strong
                lens on positioning, value props, and what actually moves the needle.
              </p>
              <p>
                Today, I partner with founders and product teams to define the right problems, architect
                clear user journeys, and translate them into scalable, performant web experiences.
              </p>
              <p>
                Based in New York City, I&apos;ve worked across healthcare, government, fitness, and academic
                research—spaces where clarity, trust, and performance matter.
              </p>
            </div>
          </div>

          {/* Education & Certifications */}
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="space-y-6 lg:col-span-1">
              <h3 className="heading-subsection">Education</h3>
              <div className="card-professional p-6 bg-blue-50 border border-blue-200">
                <p className="text-gray-700 font-medium mb-2">Bachelor&apos;s in Business Marketing</p>
                <p className="text-blue-600 font-semibold text-lg">Pennsylvania State University</p>
                <p className="text-gray-600 text-sm mt-2">
                  Foundation in consumer psychology, market research, and strategic communication.
                </p>
              </div>
            </div>

            <div className="space-y-6 lg:col-span-2">
              <h3 className="heading-subsection">Certifications</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="card-professional p-6 bg-green-50 border border-green-200">
                  <p className="text-gray-700 font-medium mb-2">UX Design Certificate</p>
                  <p className="text-green-600 font-semibold text-lg">General Assembly</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Comprehensive UX design methodology and research.
                  </p>
                </div>
                <div className="card-professional p-6 bg-purple-50 border border-purple-200">
                  <p className="text-gray-700 font-medium mb-2">UI Design Certificate</p>
                  <p className="text-purple-600 font-semibold text-lg">Shift Nudge</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Advanced visual design and modern UI patterns.
                  </p>
                </div>
                <div className="card-professional p-6 bg-slate-50 border border-slate-200">
                  <p className="text-gray-700 font-medium mb-2">Fundamentals of Building AI Agents</p>
                  <p className="text-blue-600 font-semibold text-lg">IBM</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Core principles of AI agent design and practical implementation.
                  </p>
                </div>
                <div className="card-professional p-6 bg-slate-50 border border-slate-200">
                  <p className="text-gray-700 font-medium mb-2">Responsible AI: Applying AI Principles</p>
                  <p className="text-green-600 font-semibold text-lg">Google</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Applying fairness, transparency, and accountability in AI systems.
                  </p>
                </div>
                <div className="card-professional p-6 bg-slate-50 border border-slate-200">
                  <p className="text-gray-700 font-medium mb-2">Foundation AI and Data Science</p>
                  <p className="text-emerald-600 font-semibold text-lg">Linux</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Fundamentals of AI, machine learning, and data science workflows.
                  </p>
                </div>
                <div className="card-professional p-6 bg-slate-50 border border-slate-200">
                  <p className="text-gray-700 font-medium mb-2">A.I. for Everyone</p>
                  <p className="text-indigo-600 font-semibold text-lg">DeepLearning.AI</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Non-technical overview of how AI creates value in organizations.
                  </p>
                </div>
                <div className="card-professional p-6 bg-slate-50 border border-slate-200">
                  <p className="text-gray-700 font-medium mb-2">AI Ethics</p>
                  <p className="text-blue-600 font-semibold text-lg">IBM</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Ethical considerations and governance for AI-driven products.
                  </p>
                </div>
                <div className="card-professional p-6 bg-slate-50 border border-slate-200">
                  <p className="text-gray-700 font-medium mb-2">Elements of AI</p>
                  <p className="text-rose-600 font-semibold text-lg">University of Helsinki</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Foundational concepts in AI, from basics to real-world impact.
                  </p>
                </div>
                <div className="card-professional p-6 bg-slate-50 border border-slate-200">
                  <p className="text-gray-700 font-medium mb-2">Foundations of Project Management</p>
                  <p className="text-green-600 font-semibold text-lg">Google</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Planning, scoping, and delivering projects with cross-functional teams.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Expertise */}
      <section className="section-padding bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 space-y-8">
          <div className="text-center space-y-3">
            <h2 className="heading-section">Skills & Expertise</h2>
            <p className="text-body max-w-3xl mx-auto">
              A mix of UX craft, AI literacy, and delivery skills that help take products from idea to
              launched experiences.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(skills).map(([groupName, groupSkills]) => (
              <div key={groupName} className="space-y-4">
                <h3 className="text-sm font-semibold tracking-wide text-slate-700 uppercase">
                  {groupName}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {groupSkills.map((skill) => (
                    <span key={skill} className="skill-badge">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Timeline */}
      <ProfessionalTimeline />

      {/* Design Philosophy Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow text-center">
          <h2 className="heading-section mb-12">Design Philosophy</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-professional p-8 bg-white">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">User-Centered</h3>
              <p className="text-gray-600">
                Every design decision starts with understanding real user needs, behaviors, and pain points
                through research and empathy.
              </p>
            </div>

            <div className="card-professional p-8 bg-white">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Data-Driven</h3>
              <p className="text-gray-600">
                Design choices are validated through testing, analytics, and user feedback to ensure
                measurable improvements.
              </p>
            </div>

            <div className="card-professional p-8 bg-white">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Iterative</h3>
              <p className="text-gray-600">
                Continuous improvement through prototyping, testing, and refinement leads to better user
                experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container-narrow text-center">
          <h2 className="text-3xl font-bold mb-6">Let&apos;s Work Together</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            I collaborate with teams who care about both craft and outcomes—whether you&apos;re refining an
            existing product or taking something entirely new to market.
          </p>
          <div className="space-y-6">
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-2 bg-white/20 rounded-full px-6 py-3">
                <span className="text-2xl">🤝</span>
                <span className="font-medium">Strategy & UX Partnership</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 rounded-full px-6 py-3">
                <span className="text-2xl">🧠</span>
                <span className="font-medium">AI-Augmented Experiences</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 rounded-full px-6 py-3">
                <span className="text-2xl">🚀</span>
                <span className="font-medium">From Idea to Launch</span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-semibold">Open to lead UX/web strategy roles and advisory work.</p>
              <p className="opacity-75">New York City • Remote & Hybrid</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
